import React, { useEffect, useMemo, useRef, useState } from 'react'
import { printDate } from '../../../helpers/Utils'
import { IElementProps } from '../../../types/interfaces'

const DatePick = (props: IElementProps) => {
  const handleChangeInt = (name: string, e: Date | [Date, Date] | null) => {
    const event = {
      target: { value: printDate(Array.isArray(e) ? (e as [Date, Date])[0] : (e as Date)) },
    } as unknown as React.ChangeEvent<HTMLInputElement>
    props.handleChange(name, event)
  }

  const startDate = useMemo(() => new Date(), [])

  useEffect(() => {
    if (props.visible && !props.value) {
      handleChangeInt(props.name, startDate)
    }
  }, [props.visible])

  const processDate = (date: string) => {
    const [day, month, year] = date.split('.').map(Number)
    if (day && month && year && date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      return new Date(year, month - 1, day)
    }
    return new Date(date)
  }

  const pad = (n: number) => String(n).padStart(2, '0')
  const toInputValue = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  const toDisplayValue = (date: Date) => `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`

  const selectedDate = props.value ? processDate(props.value) : startDate
  const readOnly = props.formReadOnly || props.disabledInput
  const nativeRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState<string | null>(null)

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [year, month, day] = e.target.value.split('-').map(Number)
    handleChangeInt(props.name, e.target.value ? new Date(year, month - 1, day) : null)
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    setDraft(text)
    if (!text) {
      handleChangeInt(props.name, null)
      return
    }
    const match = text.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
    if (!match) return
    const [day, month, year] = match.slice(1).map(Number)
    const date = new Date(year, month - 1, day)
    if (date.getDate() === day && date.getMonth() === month - 1) {
      handleChangeInt(props.name, date)
    }
  }

  const openPicker = () => {
    if (!readOnly) nativeRef.current?.showPicker()
  }

  return (
    <>
      {props.visible && (
        <div className="formGroup">
          <div className="labelTooltip">
            {props.translatedTooltip && (
              <>
                <div className="tooltipArea">
                  <i className="icon-info"></i>
                  <div className="tooltip">{props.translatedTooltip}</div>
                </div>
              </>
            )}
            <label>{props.label}</label>
          </div>
          <div className="row">
            <div className="col-6">
              <div className="dateArea required-highlight">
                <input
                  type="text"
                  readOnly={readOnly}
                  value={draft ?? toDisplayValue(selectedDate)}
                  onChange={handleTextChange}
                  onClick={openPicker}
                  onBlur={() => setDraft(null)}
                />
                <input
                  ref={nativeRef}
                  type="date"
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: 0,
                    height: 0,
                    padding: 0,
                    border: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                  tabIndex={-1}
                  aria-hidden
                  value={toInputValue(selectedDate)}
                  onChange={handleNativeChange}
                />
                <i className="icon-calendar" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
export default DatePick
